import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ChevronDown, Menu, X } from "lucide-react";
import { nav, company } from "../mock/mock";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      {/* Top bar */}
      <div className="hidden md:block brand-gradient text-white/90 text-sm">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-10">
          <span className="text-white/80">
            North Carolina's Best Contact Centre Outsourcing — 30-Day Money-Back Guarantee
          </span>
          <a
            href={`tel:${company.phoneRaw}`}
            className="flex items-center gap-2 font-semibold hover:text-gold-light transition-colors"
          >
            <Phone size={15} /> {company.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-[74px]">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/logo.webp"
              alt="Mid Atlantic Business Management logo"
              className="w-12 h-12 object-contain transition-transform group-hover:scale-105"
            />
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-navy text-[15px] tracking-tight">
                Mid Atlantic
              </div>
              <div className="text-[11px] text-muted-foreground font-semibold tracking-wide uppercase">
                Business Management
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((item) =>
              item.children ? (
                <div key={item.label} className="relative group">
                  <button className="flex items-center gap-1 px-3 py-2 text-[13.5px] font-semibold text-navy/80 hover:text-navy transition-colors">
                    {item.label}
                    <ChevronDown size={14} className="mt-0.5 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                    <div className="w-64 bg-white rounded-xl shadow-xl border border-border overflow-hidden">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-5 py-3 text-sm font-medium text-navy/80 hover:bg-secondary hover:text-navy border-l-2 border-transparent hover:border-gold transition-all"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-2 text-[13.5px] font-semibold transition-colors ${
                    isActive(item.to) ? "text-gold-dark" : "text-navy/80 hover:text-navy"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden sm:inline-flex items-center rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-navy-dark hover:bg-gold-light transition-colors shadow-sm"
            >
              Free Consultation
            </Link>
            <button
              className="lg:hidden text-navy p-1"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {nav.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                    className="w-full flex items-center justify-between py-3 text-[15px] font-semibold text-navy"
                  >
                    {item.label}
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${openGroup === item.label ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openGroup === item.label && (
                    <div className="pl-4 pb-2 space-y-1">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block py-2 text-sm font-medium text-navy/70"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className="block py-3 text-[15px] font-semibold text-navy border-b border-border/60"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href={`tel:${company.phoneRaw}`}
              className="flex items-center gap-2 py-3 font-bold text-navy-light"
            >
              <Phone size={16} /> {company.phone}
            </a>
            <Link
              to="/contact"
              className="block text-center rounded-md bg-gold px-5 py-3 text-sm font-bold text-navy-dark mt-2"
            >
              Book Free Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
